// Account settings, verify email, reset password, first-run welcome - ported
// from the design source (screens-account.jsx) and bound to the live
// services: Cognito flows, profile/team/token endpoints, and the public
// developer profile.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { toast } from '../design/toast';

/* ── Token name modal ─────────────────────────────────────────────────── */
const TOKEN_NAME_MAX = 64;

function TokenNameModal({
  onConfirm,
  onCancel,
}: {
  onConfirm: (name: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onCancel(); }
      if (e.key === 'Enter' && name.trim()) { e.stopPropagation(); onConfirm(name.trim()); }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [name, onCancel, onConfirm]);

  return (
    <div
      onClick={onCancel}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(4,4,6,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="New API token"
        style={{
          width: 380, background: 'var(--ss-bg-2)', border: '1px solid var(--ss-border)',
          borderRadius: 14, padding: '22px 24px 18px', boxShadow: '0 30px 80px rgba(0,0,0,0.55)',
          display: 'flex', flexDirection: 'column', gap: 16,
        }}
      >
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>New API token</h3>
          <p style={{ fontSize: 12.5, color: 'var(--ss-text-3)', lineHeight: 1.5 }}>
            Give the token a name so you remember what it's for.
          </p>
        </div>
        <div className="ss-field">
          <span className="ss-field-label">Token name</span>
          <input
            ref={inputRef}
            className="ss-input"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, TOKEN_NAME_MAX))}
            placeholder="e.g. My CI Build Token"
            maxLength={TOKEN_NAME_MAX}
          />
          <span style={{ fontSize: 11, color: 'var(--ss-text-3)', marginTop: 4 }}>
            {name.length}/{TOKEN_NAME_MAX}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="ss-btn ss-btn--ghost" style={{ padding: '7px 14px', fontSize: 12.5, borderRadius: 9 }} onClick={onCancel}>
            Cancel
          </button>
          <button
            className="ss-btn ss-btn--primary"
            style={{ padding: '7px 14px', fontSize: 12.5, borderRadius: 9 }}
            disabled={!name.trim()}
            onClick={() => onConfirm(name.trim())}
          >
            Create token
          </button>
        </div>
      </div>
    </div>
  );
}
import { useLocation } from 'react-router-dom';

import { useAuth } from '../auth/AuthProvider';
import { AppStage, AppTopNav, AuthTopBar, initialsOf, TEMPLATE_CHOICES, templateForcedDimension, timeAgo } from '../app/screens-app';
import { ImageSlot } from '../components/ImageSlot';
import { Glyph } from '../components/ui-shared';
import { VerifyWidget, useVerifyGate } from '../components/VerifyWidget';
import { Link, navigate } from '../design/router';
import { createProject } from '../persistence/projectRepository';
import {
  confirmPasswordReset,
  confirmRegistration,
  loginAfterConfirmation,
  requestPasswordReset,
  resendConfirmation,
} from '../services/auth';
import { SITE_KEY_AUTH } from '../services/captcha';
import { getDevProfile, updateDevProfile, type DevProfileSettings } from '../services/devProfile';
import {
  createToken,
  deleteAccount,
  getProfile,
  getTeam,
  listLinkedGames,
  listTokens,
  revokeToken,
  unlinkGame,
  updateProfile,
  updateTeam,
  type ApiToken,
  type LinkedGame,
  type Profile,
  type Team,
} from '../services/settings';
import { PjIcon, PJ_ICONS } from '../project/screens-project';
import { EdConfirmDialog, EdTypedConfirmDialog } from '../editor/editor-panels';
import { TabStrip, panelId, type TabDescriptor } from '../../engine/editor/components/Tabs';

function ErrorLine({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p style={{ color: 'var(--ss-danger)', fontSize: 13, lineHeight: 1.5 }} role="alert">
      {message}
    </p>
  );
}

/* ── Account settings ─────────────────────────────────────────────────── */
function AccountProfile({ profile, onSaved }: { profile: Profile | null; onSaved: () => void }) {
  const [name, setName] = useState(profile?.display_name ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setName(profile?.display_name ?? '');
  }, [profile]);

  async function save() {
    setError(null);
    try {
      await updateProfile(name.trim());
      onSaved();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your profile.');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="set-card">
        <h3>Profile</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div className="app-avatar" style={{ width: 56, height: 56, fontSize: 18 }}>
            {initialsOf(profile?.display_name || profile?.email || '?')}
          </div>
          <div>
            <span className="hint">Avatars come from your initials for now. Image avatars arrive with public profiles.</span>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div className="ss-field">
            <span className="ss-field-label">Display name</span>
            <input className="ss-input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="ss-field">
            <span className="ss-field-label">Email</span>
            <input className="ss-input" value={profile?.email ?? ''} disabled style={{ opacity: 0.7 }} />
          </div>
        </div>
        <ErrorLine message={error} />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="ss-btn ss-btn--primary ss-btn--sm" onClick={save}>
            {saved ? 'Saved' : 'Save changes'}
          </button>
        </div>
      </div>
      <div className="set-card">
        <h3>Password</h3>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <span className="hint">Reset it any time from the sign-in flow.</span>
          <Link to="/reset" className="ss-btn ss-btn--ghost ss-btn--sm" style={{ flexShrink: 0 }}>
            Change password
          </Link>
        </div>
      </div>
    </div>
  );
}

function AccountBilling() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="set-card" style={{ textAlign: 'center', padding: '40px 32px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 48, height: 48, borderRadius: 14,
            background: 'var(--ss-accent-soft)', border: '1px solid var(--ss-accent-border)',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6L12 2z" stroke="var(--ss-accent)" strokeWidth="1.5" strokeLinejoin="round" />
            </svg>
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <h2 style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em' }}>
              Plans &amp; Pricing - Coming Soon
            </h2>
            <p style={{ fontSize: 14.5, color: 'var(--ss-text-2)', maxWidth: 420, margin: '0 auto', lineHeight: 1.65 }}>
              Soulstack Studio will offer a free tier plus paid plans with expanded storage, more games, and team features.
              We'll notify you when plans launch.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginTop: 4 }}>
            <a
              href="mailto:hello@sstk.studio?subject=Pricing%20Updates"
              className="ss-btn ss-btn--primary"
              style={{ minWidth: 180, justifyContent: 'center' }}
            >
              Notify me
            </a>
            <span style={{ fontSize: 12, color: 'var(--ss-text-3)' }}>
              You're on early access - everything is free while we build.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function tokenWhen(tk: ApiToken): string {
  const created = tk.created_at ? `Created ${timeAgo(new Date(tk.created_at).getTime())}` : 'Created';
  const used = tk.last_used_at ? ` · last used ${timeAgo(new Date(tk.last_used_at).getTime())}` : ' · never used';
  return created + used;
}

function AccountTokens() {
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [secret, setSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [showNameModal, setShowNameModal] = useState(false);
  const [confirmingRevokeId, setConfirmingRevokeId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listTokens()
      .then((t) => {
        if (!cancelled) setTokens(t.filter((tk) => !tk.revoked));
      })
      .catch(() => {
        if (!cancelled) setError('Could not load tokens.');
      });
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  async function handleCreateToken(name: string) {
    setShowNameModal(false);
    setError(null);
    try {
      const created = await createToken(name);
      setSecret(created.token);
      setNonce((n) => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the token.');
    }
  }

  async function revoke(id: string) {
    setConfirmingRevokeId(null);
    try {
      await revokeToken(id);
      setNonce((n) => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not revoke the token.');
    }
  }

  return (
    <div className="set-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h3>API & registry tokens</h3>
          <span className="hint">For CI pipelines and the @sstk package registry.</span>
        </div>
        <button className="ss-btn ss-btn--primary ss-btn--sm" onClick={() => setShowNameModal(true)}>
          New token
        </button>
      </div>
      {secret && (
        <div className="ss-field" style={{ background: 'var(--ss-accent-soft)', borderRadius: 10, padding: 12 }}>
          <span className="ss-field-label" style={{ color: 'var(--ss-accent)' }}>
            Copy your new token now - it is shown once
          </span>
          <input className="ss-input" readOnly value={secret} style={{ fontFamily: 'var(--ss-mono)', fontSize: 12 }} onFocus={(e) => e.target.select()} />
        </div>
      )}
      <ErrorLine message={error} />
      {tokens.map((tk) => (
        <div key={tk.id} className="ss-row">
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 13.5, fontWeight: 500 }}>{tk.name}</span>
            <span style={{ fontSize: 11.5, color: 'var(--ss-text-3)' }}>{tokenWhen(tk)}</span>
          </div>
          <span
            style={{
              fontFamily: 'var(--ss-mono)',
              fontSize: 10,
              letterSpacing: '0.08em',
              color: 'var(--ss-text-3)',
              border: '1px solid var(--ss-border-soft)',
              borderRadius: 999,
              padding: '3px 9px',
            }}
          >
            {tk.prefix}…
          </span>
          <button
            className="ss-btn ss-btn--ghost"
            style={{ padding: '4px 11px', fontSize: 12, borderRadius: 7, color: 'var(--ss-danger)', borderColor: 'color-mix(in srgb, var(--ss-danger) 30%, transparent)' }}
            onClick={() => setConfirmingRevokeId(tk.id)}
          >
            Revoke
          </button>
        </div>
      ))}
      {tokens.length === 0 && <span className="hint">No tokens yet.</span>}
      <p className="hint">Tokens are shown once at creation. Treat them like passwords.</p>
      {showNameModal && (
        <TokenNameModal
          onConfirm={(name) => void handleCreateToken(name)}
          onCancel={() => setShowNameModal(false)}
        />
      )}
      {confirmingRevokeId && (
        <EdConfirmDialog
          title="Revoke this token?"
          body="Anything using it stops working immediately."
          confirmLabel="Revoke token"
          onConfirm={() => void revoke(confirmingRevokeId)}
          onCancel={() => setConfirmingRevokeId(null)}
        />
      )}
    </div>
  );
}

function AccountConnectedGames() {
  const [games, setGames] = useState<LinkedGame[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [confirmingUnlinkId, setConfirmingUnlinkId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listLinkedGames()
      .then((g) => {
        if (!cancelled) setGames(g);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load connected games.');
      });
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  async function unlink(gameId: string) {
    setConfirmingUnlinkId(null);
    try {
      await unlinkGame(gameId);
      setNonce((n) => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not disconnect the game.');
    }
  }

  return (
    <div className="set-card">
      <div>
        <h3>Connected games</h3>
        <span className="hint">Games you've signed into with this Soulstack account.</span>
      </div>
      <ErrorLine message={error} />
      {games.map((g) => (
        <div key={g.game_id} className="ss-row">
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 13.5, fontWeight: 500 }}>{g.name}</span>
            <span style={{ fontSize: 11.5, color: 'var(--ss-text-3)' }}>
              {g.linked_at ? `Connected ${timeAgo(new Date(g.linked_at).getTime())}` : 'Connected'}
            </span>
          </div>
          <button
            className="ss-btn ss-btn--ghost"
            style={{ padding: '4px 11px', fontSize: 12, borderRadius: 7, color: 'var(--ss-danger)', borderColor: 'color-mix(in srgb, var(--ss-danger) 30%, transparent)' }}
            onClick={() => setConfirmingUnlinkId(g.game_id)}
          >
            Disconnect
          </button>
        </div>
      ))}
      {games.length === 0 && <span className="hint">No games connected yet.</span>}
      <p className="hint">Disconnecting keeps your saved progress - reconnecting later picks up right where you left off.</p>
      {confirmingUnlinkId && (
        <EdConfirmDialog
          title="Disconnect this game?"
          body="Your saved progress is kept. You can reconnect any time from the game's own play page."
          confirmLabel="Disconnect"
          onConfirm={() => void unlink(confirmingUnlinkId)}
          onCancel={() => setConfirmingUnlinkId(null)}
        />
      )}
    </div>
  );
}

function AccountTeam({ profile }: { profile: Profile | null }) {
  const [team, setTeam] = useState<Team | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getTeam()
      .then((t) => {
        if (!cancelled) {
          setTeam(t);
          setName(t.name);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          console.error('[AccountTeam] failed to load team:', err);
          setError('Could not load team information.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function save() {
    setError(null);
    try {
      const t = await updateTeam(name.trim());
      setTeam(t);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not rename the team.';
      setError(msg);
      toast(msg, 'error');
    }
  }

  return (
    <div className="set-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h3>Team</h3>
          <span className="hint">{team ? `${team.name} · 1 seat used` : 'Who shares your tenant'}</span>
        </div>
      </div>
      <div className="ss-field">
        <span className="ss-field-label">Team name</span>
        <div style={{ display: 'flex', gap: 10 }}>
          <input className="ss-input" value={name} onChange={(e) => setName(e.target.value)} />
          <button className="ss-btn ss-btn--ghost ss-btn--sm" style={{ flexShrink: 0 }} onClick={save}>
            {saved ? 'Saved' : 'Save'}
          </button>
        </div>
      </div>
      <ErrorLine message={error} />
      {profile && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="app-avatar" style={{ width: 30, height: 30, fontSize: 11 }}>
            {initialsOf(profile.display_name || profile.email)}
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 13.5, fontWeight: 500 }}>{profile.display_name || profile.email}</span>
            <span style={{ fontSize: 11.5, color: 'var(--ss-text-3)' }}>{profile.email}</span>
          </div>
          <span style={{ fontSize: 12, color: 'var(--ss-text-3)' }}>Owner</span>
        </div>
      )}
      <span className="hint">More seats arrive with team plans.</span>
    </div>
  );
}

function AccountDanger() {
  const { signOut } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  async function destroy() {
    setConfirming(false);
    try {
      await deleteAccount();
      await signOut();
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the account.');
    }
  }

  return (
    <div className="set-card" style={{ borderColor: 'color-mix(in srgb, var(--ss-danger) 25%, transparent)' }}>
      <h3 style={{ color: 'var(--ss-danger)' }}>Delete account</h3>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <span className="hint">Deletes your account, projects, published games and tokens after a 14-day grace period.</span>
        <button
          className="ss-btn ss-btn--ghost ss-btn--sm"
          style={{ flexShrink: 0, borderColor: 'color-mix(in srgb, var(--ss-danger) 40%, transparent)', color: 'var(--ss-danger)' }}
          onClick={() => setConfirming(true)}
        >
          Delete account
        </button>
      </div>
      <ErrorLine message={error} />
      {confirming && (
        <EdTypedConfirmDialog
          title="Delete your account?"
          body="Deletes your account, projects, published games and tokens after a 14-day grace period."
          confirmLabel="Delete account"
          requiredText="DELETE"
          onConfirm={() => void destroy()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  );
}

function AccountDevProfile() {
  const [draft, setDraft] = useState<DevProfileSettings>({ studio_name: '', slug: '', bio: '', website: '' });
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getDevProfile()
      .then((p) => {
        if (!cancelled && p) setDraft(p);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('[AccountDevProfile] failed to load dev profile:', err);
        setError("Couldn't load your public profile. Reload and try again before making changes here - saving now could overwrite what's already published.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function save() {
    setError(null);
    try {
      const p = await updateDevProfile(draft);
      setDraft(p);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your public profile.');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="set-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3>Public profile</h3>
          {draft.slug && (
            <Link to={`/dev/${draft.slug}`} className="ss-btn ss-btn--ghost ss-btn--sm">
              View public page
            </Link>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '104px 1fr', gap: 16, alignItems: 'start' }}>
          <ImageSlot shape="rounded" radius={16} placeholder="Avatar" style={{ width: 104, height: 104, display: 'block' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="ss-field">
                <span className="ss-field-label">Studio name</span>
                <input
                  className="ss-input"
                  value={draft.studio_name}
                  onChange={(e) => setDraft({ ...draft, studio_name: e.target.value })}
                />
              </div>
              <div className="ss-field">
                <span className="ss-field-label">Profile URL</span>
                <div className="ss-input" style={{ display: 'flex', alignItems: 'center', gap: 0, padding: '0 0 0 12px', fontFamily: 'var(--ss-mono)', fontSize: 12.5 }}>
                  <span style={{ color: 'var(--ss-text-3)', flexShrink: 0, userSelect: 'none' }}>sstk.studio/dev/</span>
                  <input
                    value={draft.slug}
                    placeholder="your-studio"
                    // The displayed value is only ever the slug itself now -
                    // previously this field showed the full prefixed URL but
                    // re-derived the slug by splitting on '/', so the value
                    // jumped to include the prefix on every keystroke and the
                    // cursor landed mid-prefix, corrupting whatever was typed
                    // after the first character.
                    onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
                    style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', color: 'inherit', font: 'inherit', padding: '10px 12px 10px 0', minWidth: 0 }}
                  />
                </div>
              </div>
            </div>
            <div className="ss-field">
              <span className="ss-field-label">Bio</span>
              <textarea
                className="ss-input"
                rows={3}
                value={draft.bio}
                onChange={(e) => setDraft({ ...draft, bio: e.target.value })}
                style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 13.5 }}
              />
            </div>
            <div className="ss-field">
              <span className="ss-field-label">Website</span>
              <input className="ss-input" value={draft.website} onChange={(e) => setDraft({ ...draft, website: e.target.value })} />
            </div>
          </div>
        </div>
        <div className="ss-field">
          <span className="ss-field-label">Banner</span>
          <ImageSlot shape="rounded" radius={12} placeholder="Studio banner" style={{ width: '100%', height: 110, display: 'block' }} />
        </div>
        <ErrorLine message={error} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="hint">Published games appear on your page automatically. Hide any from its Project settings.</span>
          <button className="ss-btn ss-btn--primary ss-btn--sm" style={{ flexShrink: 0 }} onClick={save}>
            {saved ? 'Saved' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ScreenAccount() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getProfile()
      .then((p) => {
        if (!cancelled) setProfile(p);
      })
      .catch((err: unknown) => {
        if (!cancelled) toast(err instanceof Error ? `Couldn't load your profile: ${err.message}` : "Couldn't load your profile.", 'error');
      });
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  const sections: { id: string; label: string; sub: string; el: ReactNode }[] = [
    { id: 'profile', label: 'Profile', sub: 'Your identity across Soulstack', el: <AccountProfile profile={profile} onSaved={() => setNonce((n) => n + 1)} /> },
    { id: 'devprofile', label: 'Public profile', sub: 'Your developer page in the showcase', el: <AccountDevProfile /> },
    { id: 'billing', label: 'Billing & plan', sub: 'Subscription, usage and invoices', el: <AccountBilling /> },
    { id: 'tokens', label: 'API tokens', sub: 'Programmatic access for CI and the registry', el: <AccountTokens /> },
    { id: 'games', label: 'Connected games', sub: 'Games signed in with your Soulstack account', el: <AccountConnectedGames /> },
    { id: 'team', label: 'Team', sub: 'Who shares your tenant', el: <AccountTeam profile={profile} /> },
    { id: 'danger', label: 'Danger zone', sub: 'Irreversible actions live here', el: <AccountDanger /> },
  ];
  const location = useLocation();
  const requestedSection = location.hash.replace('#', '');
  const [active, setActive] = useState(
    sections.some((s) => s.id === requestedSection) ? requestedSection : 'profile',
  );
  const current = sections.find((s) => s.id === active) ?? sections[0]!;

  return (
    <AppStage fill>
      <AppTopNav />
      <div className="pj-shell">
        <div className="pj-rail">
          <Link to="/app" className="pj-rail-link" style={{ color: 'var(--ss-text-3)', marginBottom: 10 }}>
            <PjIcon d={PJ_ICONS.back!} /> All projects
          </Link>
          <div style={{ padding: '0 12px 14px' }}>
            <span style={{ fontSize: 15.5, fontWeight: 600 }}>Account</span>
          </div>
          <TabStrip
            listLabel="Account settings"
            variant="rail"
            tabs={sections.map((s): TabDescriptor => ({ id: s.id, label: s.label }))}
            activeId={active}
            onSelect={setActive}
            controlsPanels
            // Nested inside the surrounding .pj-rail shell (which already
            // supplies the sidebar's width/border/background/padding for the
            // "All projects" link and "Account" heading above) - cancel out
            // this variant's own copy of that same box so it doesn't double
            // up, keeping only its flex-column/gap layout for the tabs.
            style={{ width: 'auto', padding: 0, border: 'none', background: 'transparent' }}
          />
        </div>
        <div className="pj-main">
          <div className="pj-page" style={{ maxWidth: 720 }}>
            <div className="pj-page-head">
              <div>
                <h1>{current.label}</h1>
                <p>{current.sub}</p>
              </div>
            </div>
            <div id={panelId(active)}>{current.el}</div>
          </div>
        </div>
      </div>
    </AppStage>
  );
}

/* ── Verify email ─────────────────────────────────────────────────────── */
export function ScreenVerifyEmail() {
  const location = useLocation();
  const state = (location.state ?? {}) as { email?: string; password?: string };
  const email = state.email ?? '';
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resent, setResent] = useState(false);
  // Resend needs a Soulstack Verify token (advisory gate, see services/auth.ts).
  // Entering the emailed code is deliberately not gated, so the widget only
  // appears once Resend is clicked and the resend fires as soon as it has a token.
  const [resending, setResending] = useState(false);
  const captcha = useVerifyGate(SITE_KEY_AUTH);

  useEffect(() => {
    if (!resending || !captcha.token) return;
    const token = captcha.token;
    void (async () => {
      try {
        await resendConfirmation(email, token);
        setResent(true);
        setTimeout(() => setResent(false), 3000);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not resend the code.');
      } finally {
        // Hiding the widget also spends the token and clears it for next time.
        setResending(false);
      }
    })();
  }, [resending, captcha.token, email]);

  async function verify() {
    const code = refs.current.map((r) => r?.value ?? '').join('');
    if (code.length !== 6) {
      setError('Enter the 6-digit code.');
      return;
    }
    if (!email) {
      setError('Open this page from the sign-up flow so we know which account to verify.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await confirmRegistration(email, code);
      if (state.password) {
        await loginAfterConfirmation(email, state.password);
        navigate('/app/welcome');
      } else {
        navigate('/signin');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That code did not work. Try again.');
    } finally {
      setBusy(false);
    }
  }

  function resend() {
    if (!email) return;
    setError(null);
    setResending((waiting) => !waiting);
  }

  return (
    <AppStage fill>
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 60% 45% at 50% 0%, var(--ss-glow), transparent 70%)' }} />
      <AuthTopBar />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <div style={{ width: 420, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 26, textAlign: 'center' }}>
          <Link to="/">
            <Glyph size={44} />
          </Link>
          <div>
            <h1 className="ss-display" style={{ fontSize: 28 }}>
              Check your inbox
            </h1>
            <p style={{ color: 'var(--ss-text-3)', fontSize: 14, marginTop: 10, lineHeight: 1.6 }}>
              We sent a 6-digit code to <strong style={{ color: 'var(--ss-text-2)', fontWeight: 500 }}>{email || 'your email'}</strong>
            </p>
          </div>
          <div style={{ display: 'flex', gap: 9 }}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <input
                key={i}
                maxLength={1}
                inputMode="numeric"
                ref={(el) => {
                  refs.current[i] = el;
                }}
                onChange={(e) => {
                  if (e.target.value && refs.current[i + 1]) refs.current[i + 1]?.focus();
                }}
                className="ss-input"
                style={{ width: 48, height: 56, textAlign: 'center', fontSize: 22, fontFamily: 'var(--ss-sans)', fontWeight: 500, padding: 0 }}
              />
            ))}
          </div>
          <ErrorLine message={error} />
          <button className="ss-btn ss-btn--primary" style={{ width: 320 }} onClick={verify} disabled={busy}>
            {busy ? 'Verifying…' : 'Verify and continue'}
          </button>
          <p style={{ fontSize: 13, color: 'var(--ss-text-3)' }}>
            No code?{' '}
            <button onClick={resend} style={{ color: 'var(--ss-text)', fontWeight: 600, border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', font: 'inherit' }}>
              {resent ? 'Sent' : resending ? 'Cancel' : 'Resend'}
            </button>{' '}
            · codes expire after 10 minutes
          </p>
          {resending && <VerifyWidget gate={captcha} />}
        </div>
      </div>
    </AppStage>
  );
}

/* ── Reset password ───────────────────────────────────────────────────── */
export function ScreenResetPassword() {
  const [step, setStep] = useState<'request' | 'sent' | 'set'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Only the request step (the one that sends the email) is gated by Soulstack
  // Verify, one fresh token per attempt; see services/auth.ts for why it is
  // advisory. Confirming the emailed code is not gated.
  const verify = useVerifyGate(SITE_KEY_AUTH);

  async function request() {
    if (!verify.token) {
      setError('Wait for the verification check to finish, then try again.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await requestPasswordReset(email, verify.token);
      setStep('sent');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the reset.');
    } finally {
      setBusy(false);
      verify.renew();
    }
  }

  async function saveNew() {
    if (pw1 !== pw2) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await confirmPasswordReset(email, code, pw1);
      navigate('/signin');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not set the new password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppStage fill>
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 60% 45% at 50% 0%, var(--ss-glow), transparent 70%)' }} />
      <AuthTopBar />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <div style={{ width: 400, display: 'flex', flexDirection: 'column', gap: 26 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, textAlign: 'center' }}>
            <Link to="/">
              <Glyph size={44} />
            </Link>
            <div>
              <h1 className="ss-display" style={{ fontSize: 28 }}>
                {step === 'set' ? 'Set a new password' : 'Reset your password'}
              </h1>
              <p style={{ color: 'var(--ss-text-3)', fontSize: 14, marginTop: 8 }}>
                {step === 'request' && "Enter your email and we'll send a reset code."}
                {step === 'sent' && 'Code sent. It expires in 30 minutes.'}
                {step === 'set' && 'Minimum 10 characters, ideally a phrase.'}
              </p>
            </div>
          </div>
          <div className="ss-card" style={{ padding: 32, display: 'flex', flexDirection: 'column', gap: 16, background: 'var(--ss-panel-strong)' }}>
            {step === 'request' && (
              <>
                <div className="ss-field">
                  <span className="ss-field-label">Email</span>
                  <input className="ss-input" type="email" placeholder="you@studio.dev" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <VerifyWidget gate={verify} />
                <ErrorLine message={error} />
                <button className="ss-btn ss-btn--primary" style={{ width: '100%' }} onClick={request} disabled={busy || !email || !verify.token}>
                  {busy ? 'Sending…' : 'Send reset code'}
                </button>
              </>
            )}
            {step === 'sent' && (
              <>
                <p style={{ fontSize: 14, color: 'var(--ss-text-2)', textAlign: 'center', lineHeight: 1.6 }}>
                  Check <strong style={{ fontWeight: 600 }}>{email}</strong> for the code, then continue.
                </p>
                <button className="ss-btn ss-btn--ghost" style={{ width: '100%' }} onClick={() => setStep('set')}>
                  I have the code
                </button>
              </>
            )}
            {step === 'set' && (
              <>
                <div className="ss-field">
                  <span className="ss-field-label">Reset code</span>
                  <input className="ss-input" inputMode="numeric" placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value)} />
                </div>
                <div className="ss-field">
                  <span className="ss-field-label">New password</span>
                  <input className="ss-input" type="password" placeholder="••••••••••" value={pw1} onChange={(e) => setPw1(e.target.value)} />
                </div>
                <div className="ss-field">
                  <span className="ss-field-label">Confirm password</span>
                  <input className="ss-input" type="password" placeholder="••••••••••" value={pw2} onChange={(e) => setPw2(e.target.value)} />
                </div>
                <ErrorLine message={error} />
                <button className="ss-btn ss-btn--primary" style={{ width: '100%' }} onClick={saveNew} disabled={busy}>
                  {busy ? 'Saving…' : 'Save and sign in'}
                </button>
              </>
            )}
          </div>
          <p style={{ textAlign: 'center', fontSize: 13.5, color: 'var(--ss-text-3)' }}>
            <Link to="/signin" style={{ color: 'var(--ss-text)', fontWeight: 600 }}>
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </AppStage>
  );
}

/* ── First-run welcome (empty dashboard) ──────────────────────────────── */
/* ── First-run welcome & guided onboarding ───────────────────────────── */
export function ScreenWelcome() {
  const { user } = useAuth();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [projectName, setProjectName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(TEMPLATE_CHOICES[0]!.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = TEMPLATE_CHOICES.find((t) => t.name === selectedTemplate) ?? TEMPLATE_CHOICES[0]!;

  const features = [
    {
      title: '1. Visual map authoring',
      desc: 'Drag and drop props, place spawn points, tune lighting, and set up game logic volumes directly in the browser.',
      tag: 'EDITOR',
    },
    {
      title: '2. Instant live playtesting',
      desc: 'Hit Preview to step into first-person or top-down controls right inside the editor view - no separate build.',
      tag: 'PREVIEW',
    },
    {
      title: '3. One-click publishing',
      desc: 'Publish to get a link anyone can open in a browser. Connected players share a live presence list and whatever a Logic rule sets or decides - built for turn-based and simple co-op.',
      tag: 'PUBLISHING',
    },
  ];

  async function handleLaunch() {
    if (!user) return;
    setBusy(true);
    setError(null);
    try {
      const forcedDim = templateForcedDimension(selected.template);
      const meta = await createProject({
        ownerUid: user.id,
        name: projectName.trim() || 'Untitled world',
        template: selected.template,
        ...(forcedDim ? { projection: forcedDim } : {}),
      });
      navigate(`/app/editor/${meta.id}/edit`);
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? err.message : 'Could not create the project.');
    }
  }

  return (
    <AppStage fill>
      <AppTopNav />
      <div style={{ flex: 1, overflowY: 'auto', position: 'relative' }}>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 65% 55% at 50% 10%, var(--ss-glow), transparent 75%)' }} />
        <div
          style={{
            position: 'relative',
            maxWidth: 780,
            margin: '0 auto',
            padding: '48px 32px 64px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 32,
            textAlign: 'center',
          }}
        >
          {/* Header & Step progress */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <Glyph size={48} />
            <h1 className="ss-display" style={{ fontSize: 36, margin: 0 }}>
              {step === 1 ? 'Choose a starting point' : step === 2 ? 'How Soulstack Studio works' : 'Name your first project'}
            </h1>
            <p style={{ color: 'var(--ss-text-2)', fontSize: 15, maxWidth: 460, margin: 0 }}>
              {step === 1
                ? 'You can change everything later - this just gives you something to build outward from.'
                : step === 2
                  ? 'Build, preview and publish web games, all from the same in-browser editor.'
                  : 'Give your project a name and step straight into the editor.'}
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  style={{
                    width: 32,
                    height: 4,
                    borderRadius: 2,
                    background: s <= step ? 'var(--ss-accent)' : 'var(--ss-border)',
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </div>
          </div>

          {/* STEP 1: Template selection */}
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, width: '100%', maxWidth: 640 }}>
              {TEMPLATE_CHOICES.map((tpl) => {
                const active = selectedTemplate === tpl.name;
                return (
                  <button
                    key={tpl.name}
                    type="button"
                    onClick={() => setSelectedTemplate(tpl.name)}
                    className="ss-card"
                    style={{
                      textAlign: 'left',
                      padding: '20px 24px',
                      borderRadius: 14,
                      borderColor: active ? 'var(--ss-accent-border)' : 'var(--ss-border-soft)',
                      background: active ? 'var(--ss-accent-soft)' : 'var(--ss-hover)',
                      color: 'var(--ss-text)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                    }}
                  >
                    <span style={{ fontSize: 16, fontWeight: 600, color: active ? 'var(--ss-accent)' : 'var(--ss-text)' }}>
                      {tpl.name}
                    </span>
                    <span style={{ fontSize: 13, color: 'var(--ss-text-3)', lineHeight: 1.4 }}>{tpl.blurb}</span>
                  </button>
                );
              })}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                <button className="ss-btn ss-btn--primary ss-btn--lg" onClick={() => setStep(2)}>
                  Next: Studio tour →
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: Feature Tour */}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, width: '100%', maxWidth: 640 }}>
              <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                {features.map((feat, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: '20px 16px',
                      borderRadius: 12,
                      border: '1px solid var(--ss-border-soft)',
                      background: 'var(--ss-hover)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--ss-accent)' }}>
                      {feat.tag}
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{feat.title}</span>
                    <span style={{ fontSize: 12, color: 'var(--ss-text-3)', lineHeight: 1.45 }}>{feat.desc}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
                <button className="ss-btn ss-btn--ghost" onClick={() => setStep(1)}>
                  ← Back
                </button>
                <button className="ss-btn ss-btn--primary ss-btn--lg" onClick={() => setStep(3)}>
                  Next: Launch →
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: Project Naming & Creation */}
          {step === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, width: '100%', maxWidth: 460 }}>
              <div className="ss-field" style={{ textAlign: 'left' }}>
                <span className="ss-field-label">Project Name</span>
                <input
                  className="ss-input"
                  style={{ fontSize: 16, padding: '12px 16px' }}
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="e.g. Haven Outpost"
                  autoFocus
                />
              </div>
              <div style={{ padding: '14px 18px', borderRadius: 10, background: 'var(--ss-accent-soft)', border: '1px solid var(--ss-accent-border)', textAlign: 'left' }}>
                <span style={{ fontSize: 12.5, color: 'var(--ss-text-2)', display: 'block' }}>
                  <strong>Starting point:</strong> {selected.name}
                </span>
                <span style={{ fontSize: 11.5, color: 'var(--ss-text-3)', display: 'block', marginTop: 4 }}>
                  You can rename your project or switch game modes anytime from project settings.
                </span>
              </div>
              <ErrorLine message={error} />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                <button className="ss-btn ss-btn--ghost" onClick={() => setStep(2)} disabled={busy}>
                  ← Back
                </button>
                <button className="ss-btn ss-btn--primary ss-btn--lg" onClick={handleLaunch} disabled={busy}>
                  {busy ? 'Creating…' : 'Enter the editor'}
                </button>
              </div>
            </div>
          )}

          <Link to="/app" style={{ fontSize: 13, color: 'var(--ss-text-3)', marginTop: 8 }}>
            Skip to dashboard
          </Link>
        </div>
      </div>
    </AppStage>
  );
}
